import {
  AfterViewInit,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ActiveElement, Chart, ChartEvent, Plugin } from 'chart.js/auto';
import { DatasetDirective } from './datasets';
import { concat, merge, of, switchMap } from 'rxjs';
import { whenever } from '@/utils/whenever';
import { formatStatisticNumber } from '@/utils/number-format';

const valueLabelsPlugin: Plugin<'bar'> = {
  id: 'wrValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    ctx.save();
    ctx.fillStyle = '#2D3748'; // mirrors theme text-basic-color; canvas can't consume nb-theme()
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    ctx.font = '600 12px sans-serif';
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      if (meta.hidden) return;
      meta.data.forEach((element, index) => {
        const value = dataset.data[index] as number | null | undefined;
        if (value == null || value === 0) return;
        const pos = element.tooltipPosition(false);
        ctx.fillText(formatStatisticNumber(value), pos.x, pos.y - 4);
      });
    });
    ctx.restore();
  },
};

@Component({
  standalone: false,
  selector: 'wr-chart',
  templateUrl: './chart.component.html',
  styleUrls: ['./chart.component.scss'],
})
export class ChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() labels?: string[];

  @Input() title?: string;

  @Input() showLegend?: boolean = true;

  @Input() showValueLabels = false;

  @Input() dynamicSize = false;

  @Output() chartClicked = new EventEmitter<{ event: ChartEvent, elements: ActiveElement[], chart: Chart }>();

  @ViewChild('canvas')
  canvas!: ElementRef<HTMLCanvasElement>;

  @ContentChildren(DatasetDirective)
  datasets!: QueryList<DatasetDirective>;

  chart?: Chart;

  @HostListener('window:resize')
  onWindowResize() {
    if (!this.dynamicSize) {
      this.chart?.resize(500, 400);
    } else if (this.chart && this.dynamicSize) {
      this.resizeCanvas();
      this.chart.resize();
    }
  }

  private resizeCanvas(): void {
    const canvasElement = this.canvas.nativeElement;
    const parentElement = canvasElement.parentElement;
    if (parentElement) {
      canvasElement.width = parentElement.clientWidth;
      canvasElement.height = parentElement.clientHeight;
    }
  }

  private readonly shouldUpdate = signal(false);

  constructor() {
    whenever(this.shouldUpdate, () => {
      this.chart?.update('active');

      this.shouldUpdate.set(false);
    });
  }

  // Chromium lays out the print pass separately from the live document, so a no-arg resize()
  // in beforeprint would only re-measure the unchanged screen container. Chart.js therefore
  // recommends an explicit size for printing; afterprint restores the container-derived size.
  private readonly onBeforePrint = () => {
    this.chart?.resize(720, 400);
  };

  private readonly onAfterPrint = () => {
    this.chart?.resize();
  };

  ngAfterViewInit(): void {
    this.initChart(this.canvas.nativeElement);
    window.addEventListener('beforeprint', this.onBeforePrint);
    window.addEventListener('afterprint', this.onAfterPrint);
  }

  initChart(canvas: HTMLCanvasElement): void {
    if (this.dynamicSize) {
      this.resizeCanvas();
    }
    this.chart = new Chart(canvas, {
      data: {
        datasets: this.datasets.map((factory) => factory.getDataset()),
        labels: this.labels,
      },
      options: {
        resizeDelay: 100,
        layout: {
          padding: { top: this.showValueLabels ? 18 : 0 },
        },
        // Only override axis-tick formatting where we already format bar-top value labels
        // (showValueLabels) — otherwise leave Chart.js's default Intl-based tick formatting
        // alone for other consumers of this shared component.
        scales: this.showValueLabels ? {
          y: {
            ticks: {
              callback: (value) => formatStatisticNumber(Number(value)),
            },
          },
        } : undefined,
        plugins: {
          legend: {
            display: this.showLegend,
          },
          tooltip: {
            position: 'average',
            xAlign: 'left',
          }
        },
        onClick: (event, elements, chart) => {
          this.chartClicked.emit({ event, elements, chart });
        },
      },
      plugins: this.showValueLabels ? [valueLabelsPlugin as Plugin] : [],
    });

    concat(of(undefined), this.datasets.changes)
      .pipe(
        switchMap(() =>
          merge(
            ...this.datasets.map((factory, index) =>
              merge(of(index), factory.onChange.pipe(switchMap(() => of(index)))),
            ),
          ),
        ),
      )
      .subscribe((index) => {
        const chart = this.chart;
        if (!chart) return;
        const datasetFactory = this.datasets.get(index)!;
        Object.assign(chart.data.datasets[index], datasetFactory.getDataset());
        this.shouldUpdate.set(true);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['labels'] && this.chart) {
      this.chart.data.labels = changes['labels'].currentValue;
      this.shouldUpdate.set(true);
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeprint', this.onBeforePrint);
    window.removeEventListener('afterprint', this.onAfterPrint);
    this.chart?.destroy();
  }
}
